import {
  type CollectionReference,
  type DocumentData,
  type Query,
  type QueryDocumentSnapshot,
  Timestamp,
  collection,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore'

import { getFirebaseFirestore } from '@/lib/firebase/app'
import { callable } from '@/lib/firebase/callable'
import { type Subscription, subscribeCollection } from '@/lib/firebase/subscribe'
import { isScreenId } from '@/router/screens'
import type { SetupScope } from '@/repositories/setup-docs'
import {
  INBOX_ENTRY_KINDS,
  INBOX_FOLDERS,
  INBOX_SENDERS,
  INBOX_SUBJECT_TYPES,
  INBOX_TARGET_TABS,
  RESEARCH_RUN_KINDS,
  type InboxEntry,
  type InboxLatest,
  type InboxReadMarks,
  type InboxTab,
  type InboxTarget,
  type InboxThread,
  type NoteOrigin,
} from '@/types/inbox'
import type { NoteAttachment } from '@/types/note'
import { GATE_STAGES } from '@/types/plan'

function brandCollection(
  scope: SetupScope,
  ...segments: string[]
): CollectionReference<DocumentData> {
  return collection(
    getFirebaseFirestore(),
    'workspaces',
    scope.workspaceId,
    'brands',
    scope.brandId,
    ...segments,
  )
}

function asRecord(value: unknown): DocumentData {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as DocumentData)
    : {}
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined
}

function toDateOrNull(value: unknown): Date | null {
  return value instanceof Timestamp ? value.toDate() : null
}

function toDate(value: unknown): Date {
  return toDateOrNull(value) ?? new Date(0)
}

function toTarget(value: unknown): InboxTarget | null {
  const data = asRecord(value)
  if (typeof data.screen !== 'string' || !isScreenId(data.screen)) return null
  const tab = oneOf(data.tab, INBOX_TARGET_TABS)
  return {
    screen: data.screen,
    ...(typeof data.item === 'string' && data.item !== '' ? { item: data.item } : {}),
    ...(tab ? { tab } : {}),
  }
}

function toLatest(value: unknown): InboxLatest | undefined {
  const data = asRecord(value)
  const entry = toInboxEntry('latest', {
    kind: data.kind,
    sender: data.sender,
    params: data.params,
  })
  if (!entry) return undefined
  return {
    kind: entry.kind,
    params: entry.params,
    sender: entry.sender,
    snippet: typeof data.snippet === 'string' ? data.snippet : null,
  } as InboxLatest
}

/** The thread as the client reads it; a thread of a kind or subject the client does not know is dropped. */
export function toInboxThread(id: string, data: DocumentData): InboxThread | undefined {
  const subject = asRecord(data.subject)
  const subjectType = oneOf(subject.type, INBOX_SUBJECT_TYPES)
  const latest = toLatest(data.latest)
  if (!subjectType || !latest) return undefined
  return {
    id,
    subject: { type: subjectType, id: asString(subject.id) },
    folder: oneOf(data.folder, INBOX_FOLDERS) ?? 'inbox',
    actionNeeded: data.actionNeeded === true,
    archivedAt: toDateOrNull(data.archivedAt),
    lastEntryAt: toDate(data.lastEntryAt),
    createdAt: toDate(data.createdAt),
    latest,
  }
}

function toAttachments(value: unknown): NoteAttachment[] {
  if (!Array.isArray(value)) return []
  const attachments: NoteAttachment[] = []
  for (const item of value) {
    const attachment = asRecord(item)
    if (attachment.kind === 'libraryItem' && typeof attachment.itemId === 'string') {
      attachments.push({ kind: 'libraryItem', itemId: attachment.itemId })
    } else if (
      attachment.kind === 'link' &&
      typeof attachment.id === 'string' &&
      typeof attachment.url === 'string'
    ) {
      attachments.push({
        kind: 'link',
        id: attachment.id,
        url: attachment.url,
        ...(typeof attachment.label === 'string' ? { label: attachment.label } : {}),
      })
    }
  }
  return attachments
}

/** The entry as the client reads it; an entry of a kind the client does not know is dropped. */
export function toInboxEntry(id: string, data: DocumentData): InboxEntry | undefined {
  const kind = oneOf(data.kind, INBOX_ENTRY_KINDS)
  if (!kind) return undefined
  const base = {
    id,
    sender: oneOf(data.sender, INBOX_SENDERS) ?? 'raldo',
    target: toTarget(data.target),
    createdAt: toDate(data.createdAt),
  } as const
  const params = asRecord(data.params)
  switch (kind) {
    case 'article.questions':
      return {
        ...base,
        kind,
        params: {
          pageTitle: asString(params.pageTitle),
          questionCount: asNumber(params.questionCount),
        },
      }
    case 'article.ready':
      return {
        ...base,
        kind,
        params: {
          pageTitle: asString(params.pageTitle),
          outcome: params.outcome === 'partial' ? 'partial' : 'completed',
        },
      }
    case 'plan.proposal':
      return {
        ...base,
        kind,
        params: {
          topicsChanged: asNumber(params.topicsChanged),
          pagesAdded: asNumber(params.pagesAdded),
          pagesRemoved: asNumber(params.pagesRemoved),
          pagesChanged: asNumber(params.pagesChanged),
        },
      }
    case 'plan.topics':
      return { ...base, kind, params: { topics: asNumber(params.topics) } }
    case 'plan.reminder':
      return {
        ...base,
        kind,
        params: { stage: oneOf(params.stage, GATE_STAGES) ?? 'review-topics' },
      }
    case 'plan.ready':
      return {
        ...base,
        kind,
        params: { outcome: params.outcome === 'partial' ? 'partial' : 'completed' },
      }
    case 'post.draft':
      return {
        ...base,
        kind,
        params: {
          score: typeof params.score === 'number' ? params.score : null,
          title: asString(params.title),
          hook: asString(params.hook),
        },
      }
    case 'post.publishFailed':
      return {
        ...base,
        kind,
        params: { errorCode: asString(params.errorCode), title: asString(params.title) },
      }
    case 'post.published':
      return { ...base, kind, params: { title: asString(params.title) } }
    case 'post.results':
      return {
        ...base,
        kind,
        params: {
          title: asString(params.title),
          impressions: asNumber(params.impressions),
          reach: asNumber(params.reach),
          clicks: asNumber(params.clicks),
          engagementRate: asNumber(params.engagementRate),
        },
      }
    case 'run.failed':
      return {
        ...base,
        kind,
        params: {
          runKind: asString(params.runKind),
          errorCode: typeof params.errorCode === 'string' ? params.errorCode : null,
        },
      }
    case 'account.disconnected':
      return {
        ...base,
        kind,
        params: { platform: asString(params.platform), errorCode: asString(params.errorCode) },
      }
    case 'research.ready': {
      const runKind = oneOf(params.runKind, RESEARCH_RUN_KINDS)
      return runKind ? { ...base, kind, params: { runKind } } : undefined
    }
    case 'design.ready':
      return { ...base, kind, params: { partial: params.partial === true } }
    case 'brandStyle.ready':
      return { ...base, kind, params: { designName: asString(params.designName) } }
    case 'brandStyle.decided':
      return {
        ...base,
        kind,
        params: { decision: params.decision === 'used' ? 'used' : 'discarded' },
      }
    case 'note':
      return {
        ...base,
        kind,
        params: {},
        body: asString(data.body),
        attachments: toAttachments(data.attachments),
        origin: (data.origin === 'telegram' ? 'telegram' : 'app') satisfies NoteOrigin,
        authorUid: asString(data.authorUid),
      }
  }
}

function threadsQuery(scope: SetupScope, tab: InboxTab, count: number): Query<DocumentData> {
  const threads = brandCollection(scope, 'inbox')
  if (tab === 'archive') {
    return query(
      threads,
      where('archivedAt', '!=', null),
      orderBy('archivedAt', 'desc'),
      limit(count),
    )
  }
  return query(
    threads,
    where('folder', '==', tab),
    where('archivedAt', '==', null),
    orderBy('lastEntryAt', 'desc'),
    limit(count),
  )
}

/** Live view of one tab's newest `count` threads: Inbox and Sent are unarchived folders, Archive holds archived threads newest-archived first. */
export function subscribeInboxThreads(
  scope: SetupScope,
  tab: InboxTab,
  count: number,
): Subscription<InboxThread[]> {
  return subscribeCollection(threadsQuery(scope, tab, count), (snapshot) =>
    snapshot.docs.flatMap((thread: QueryDocumentSnapshot<DocumentData>) => {
      const mapped = toInboxThread(thread.id, thread.data())
      return mapped ? [mapped] : []
    }),
  )
}

/** How many unarchived Inbox threads the shell watches for the nav count and the notices. */
export const SHELL_THREAD_LIMIT = 200

/** Live view of the newest unarchived Inbox-folder threads, which feed the unread count and the shell notices. */
export function subscribeInboxShellThreads(scope: SetupScope): Subscription<InboxThread[]> {
  return subscribeInboxThreads(scope, 'inbox', SHELL_THREAD_LIMIT)
}

/** Live view of one thread's messages, oldest first. */
export function subscribeInboxEntries(
  scope: SetupScope,
  threadId: string,
): Subscription<InboxEntry[]> {
  return subscribeCollection(
    query(brandCollection(scope, 'inbox', threadId, 'entries'), orderBy('createdAt', 'asc')),
    (snapshot) =>
      snapshot.docs.flatMap((entry) => {
        const mapped = toInboxEntry(entry.id, entry.data())
        return mapped ? [mapped] : []
      }),
  )
}

/** Live view of when one member last read each thread; a thread with no mark is unread. */
export function subscribeInboxReadMarks(
  scope: SetupScope,
  uid: string,
): Subscription<InboxReadMarks> {
  return subscribeCollection(
    brandCollection(scope, 'inboxReaders', uid, 'read'),
    (snapshot) =>
      new Map(
        snapshot.docs.flatMap((mark): Array<[string, Date]> => {
          const readAt = toDateOrNull(mark.data().readAt)
          return readAt ? [[mark.id, readAt]] : []
        }),
      ),
  )
}

export const markInboxRead = callable<{ threadId: string }, Record<string, never>>('markInboxRead')

export const markInboxUnread = callable<{ threadId: string }, Record<string, never>>(
  'markInboxUnread',
)

export const markAllInboxRead = callable<{ threadIds: string[] }, Record<string, never>>(
  'markAllInboxRead',
)

export const archiveInboxThread = callable<{ threadId: string }, Record<string, never>>(
  'archiveInboxThread',
)

export const archiveInboxThreads = callable<{ threadIds: string[] }, Record<string, never>>(
  'archiveInboxThreads',
)

export const unarchiveInboxThread = callable<{ threadId: string }, Record<string, never>>(
  'unarchiveInboxThread',
)
